import { motion } from 'framer-motion';
import { ClipboardCheck, Coins, BadgeCheck, ShieldCheck, Database, Check, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SOLUTION } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';
import KlymartLogo from './KlymartLogo';

const FLOW_ICONS = [ClipboardCheck, Coins, BadgeCheck, ShieldCheck];
// Positions on the orbit ring (radius ≈ 39% of half-container) so nodes sit
// INSIDE the diagram box and never collide with the legend below.
const POSITIONS = [
  { top: '11%', left: '50%' }, // 0 ASSESS  (top)
  { top: '50%', left: '89%' }, // 1 FINANCE (right)
  { top: '89%', left: '50%' }, // 2 CERTIFY (bottom)
  { top: '50%', left: '11%' }, // 3 TRUST   (left)
];

export default function Solution() {
  const { m } = useI18n();
  const s = m.solution;

  return (
    <section id="solution" data-testid={SOLUTION.root} className="relative section">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, hsla(86,70%,62%,0.3), transparent)' }}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading block — stacked, no overlap */}
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {s.eyebrow}
          </div>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-[44px] tracking-[-0.015em] leading-[1.05]">
            {s.heading}<span className="text-primary">{s.headingEm}</span>
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-2xl">
            {s.lead}
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-start">
          {/* Helps list */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-primary/80 font-medium">
              {s.helpsTitle}
            </div>
            <ul className="mt-3 space-y-2.5">
              {s.helps.map((h, i) => (
                <motion.li
                  key={h.t}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5 card-hover hover:border-primary/30"
                >
                  <div className="mt-0.5 h-6 w-6 rounded-md border border-primary/40 bg-primary/10 inline-flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-foreground/95">{h.t}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{h.d}</div>
                  </div>
                </motion.li>
              ))}
            </ul>

            <div className="mt-5 rounded-xl border border-primary/30 bg-primary/[0.06] p-4">
              <div className="font-display text-lg leading-snug flex items-start gap-2">
                <ArrowRight className="h-5 w-5 mt-1 text-primary shrink-0" />
                <span>{s.tagline}</span>
              </div>
            </div>
          </div>

          {/* Clean circular flow diagram — just nodes, no orphan descriptions */}
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[380px] aspect-square mx-auto">
              {/* dashed orbit */}
              <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full orbit-spin opacity-60">
                <circle cx="200" cy="200" r="156" fill="none" stroke="hsla(86,70%,62%,0.35)" strokeDasharray="4 8" strokeWidth="1" />
              </svg>
              {/* Inner circle */}
              <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full opacity-50">
                <circle cx="200" cy="200" r="96" fill="none" stroke="hsla(150,45%,38%,0.3)" strokeWidth="1" />
              </svg>
              {/* center node */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center">
                <div className="relative h-24 w-24 mx-auto rounded-full border border-primary/40 bg-secondary flex items-center justify-center pulse-ring">
                  <KlymartLogo size={48} />
                </div>
                <div className="mt-2 font-display text-base leading-tight">Klymart</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-primary mt-0.5">
                  Finance · Verify · Grow
                </div>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-2 py-0.5">
                  <Database className="h-2.5 w-2.5 text-accent" />
                  <span className="text-[9px] uppercase tracking-wider text-accent">Data layer</span>
                </div>
              </div>
              {/* 4 outer nodes */}
              {s.flow.map((node, i) => {
                const pos = POSITIONS[i];
                const Icon = FLOW_ICONS[i];
                return (
                  <motion.div
                    key={node.tag}
                    initial={{ opacity: 0, scale: 0.85 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                    style={{ top: pos.top, left: pos.left, transform: 'translate(-50%, -50%)' }}
                    className="absolute z-20 flex flex-col items-center"
                    data-testid={`${SOLUTION.flowPrefix}-${node.tag.toLowerCase()}`}
                  >
                    <div className="h-12 w-12 rounded-full border border-primary/50 bg-secondary flex items-center justify-center text-primary shadow-[0_0_24px_-6px_hsla(86,70%,62%,0.5)]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="mt-1.5 rounded-full bg-background border border-border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider whitespace-nowrap">
                      {node.tag}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* 4-step descriptions BELOW the circle (no overlap) */}
            <div className="mt-12 grid grid-cols-2 gap-2.5 w-full max-w-md">
              {s.flow.map((node, i) => {
                const Icon = FLOW_ICONS[i];
                return (
                  <div
                    key={`legend-${node.tag}`}
                    className="rounded-lg border border-border bg-secondary/30 p-2.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 text-primary" />
                      <span className="text-[10px] uppercase tracking-[0.16em] font-medium text-primary">
                        {node.tag}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
                      {node.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Audience strip */}
        <div className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            {s.audiencesTitle}
          </div>
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {s.audiences.map((a) => (
              <div
                key={a.k}
                className="rounded-xl border border-border bg-secondary/40 p-4 hover:border-primary/30 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-primary font-medium">{a.k}</div>
                <div className="mt-1 text-sm text-foreground/90 leading-snug">{a.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
